import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { Link } from "react-router-dom";
import {
  Button,
  Input,
  Label,
  Spinner,
  Table,
  FormGroup,
} from "reactstrap";
import {
  API_TIMEOUT,
  DINAS_BY_NAMA,
  DINAS_PERANGKAT_DAERAH,
  LAPORAN_BELANJA_MAMIN,
} from "../../utils";
import { BULAN_OPTIONS, bulanLabel } from "./laporanBulananConstants";
import s from "./belanjaMamin.module.scss";

const formatRupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const LaporanBulananListPage = () => {
  const currentYear = new Date().getFullYear();
  const [user, setUser] = useState(null);
  const [idPd, setIdPd] = useState("");
  const [listDinas, setListDinas] = useState([]);
  const [tahun, setTahun] = useState(String(currentYear));
  const [bulan, setBulan] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const isSuperAdmin = useMemo(
    () => String(user?.peran || user?.status || "").toLowerCase() === "super admin",
    [user]
  );

  const listTahun = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 2018; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    setUser(u);
  }, []);

  useEffect(() => {
    axios
      .get(DINAS_PERANGKAT_DAERAH, { timeout: API_TIMEOUT })
      .then((res) => setListDinas(res.data?.data || []))
      .catch(() => setListDinas([]));
  }, []);

  useEffect(() => {
    if (!user || isSuperAdmin || idPd) return;
    const namaDinas = String(user.dinas || "").trim();
    if (!namaDinas) return;
    axios
      .get(DINAS_BY_NAMA, { timeout: API_TIMEOUT, params: { nama: namaDinas } })
      .then((res) => {
        const d = res.data?.data;
        if (d?.dinas_id) setIdPd(String(d.dinas_id));
      })
      .catch(() => {
        const match = listDinas.find(
          (d) =>
            String(d.nama_dinas || "").trim().toUpperCase() ===
            namaDinas.toUpperCase()
        );
        if (match) setIdPd(String(match.dinas_id));
      });
  }, [user, isSuperAdmin, listDinas, idPd]);

  const loadData = useCallback(() => {
    setLoading(true);
    const params = { tahun };
    if (bulan) params.bulan = bulan;
    if (idPd) params.id_perangkat_daerah = idPd;
    axios
      .get(LAPORAN_BELANJA_MAMIN, { timeout: API_TIMEOUT, params })
      .then((res) => setRows(res.data?.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tahun, bulan, idPd]);

  useEffect(() => {
    if (isSuperAdmin && !idPd) {
      loadData();
      return;
    }
    if (idPd) loadData();
  }, [loadData, idPd, isSuperAdmin]);

  const handleDelete = async (row) => {
    const ok = await swal({
      title: "Hapus laporan?",
      text: `${row.nama_dinas} — ${bulanLabel(row.bulan)} ${row.tahun}`,
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${LAPORAN_BELANJA_MAMIN}/${row.id}`, {
        timeout: API_TIMEOUT,
      });
      swal("Terhapus", "Laporan berhasil dihapus", "success");
      loadData();
    } catch (err) {
      swal("Gagal", err.response?.data?.message || err.message, "error");
    }
  };

  const namaPdAktif = useMemo(() => {
    if (!idPd) return user?.dinas || "";
    const d = listDinas.find((x) => String(x.dinas_id) === String(idPd));
    return d?.nama_dinas || user?.dinas || "";
  }, [idPd, listDinas, user]);

  return (
    <div className={`animated fadeIn ${s.pageWrap}`}>
      <div className={s.heroSection}>
        <h2>Laporan Bulanan Belanja Mamin</h2>
        <p>
          Setiap perangkat daerah melaporkan belanja makan-minum per bulan beserta
          data pengelola (NIK dan desil dari DTSEN).
        </p>
      </div>

      <div className="mb-3">
        <Button tag={Link} to="/admin/makan-minum" color="link" className="p-0">
          ← Kembali ke rekap
        </Button>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>Filter laporan</div>
        <div className={s.cardBody}>
          <div className={s.toolbar}>
            {isSuperAdmin ? (
              <FormGroup style={{ minWidth: 280 }}>
                <Label>Perangkat daerah</Label>
                <Input
                  type="select"
                  value={idPd}
                  onChange={(e) => setIdPd(e.target.value)}
                >
                  <option value="">— Semua PD —</option>
                  {listDinas.map((d) => (
                    <option key={d.dinas_id} value={String(d.dinas_id)}>
                      {d.nama_dinas}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            ) : (
              <FormGroup>
                <Label>Perangkat daerah</Label>
                <Input plaintext readOnly value={namaPdAktif || "—"} />
              </FormGroup>
            )}
            <FormGroup>
              <Label>Tahun</Label>
              <Input
                type="select"
                value={tahun}
                onChange={(e) => setTahun(e.target.value)}
              >
                {listTahun.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Bulan</Label>
              <Input
                type="select"
                value={bulan}
                onChange={(e) => setBulan(e.target.value)}
              >
                <option value="">Semua bulan</option>
                {BULAN_OPTIONS.map((b) => (
                  <option key={b.value} value={String(b.value)}>
                    {b.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="align-self-end">
              <Button color="primary" tag={Link} to="/admin/makan-minum/laporan-bulanan/new">
                + Buat laporan
              </Button>
            </FormGroup>
          </div>
        </div>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>Daftar laporan</div>
        <div className={`${s.cardBody} ${s.tableWrap}`}>
          {loading ? (
            <Spinner color="primary" />
          ) : rows.length === 0 ? (
            <p className="text-muted mb-0">
              Belum ada laporan.{" "}
              <Link to="/admin/makan-minum/laporan-bulanan/new">Buat laporan baru</Link>
            </p>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  {isSuperAdmin && <th>Perangkat daerah</th>}
                  <th>Periode</th>
                  <th>Jenis pengelola</th>
                  <th className="text-end">Total belanja</th>
                  <th className="text-center">Pengelola</th>
                  <th style={{ width: 140 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{idx + 1}</td>
                    {isSuperAdmin && <td>{row.nama_dinas}</td>}
                    <td>
                      {bulanLabel(row.bulan)} {row.tahun}
                    </td>
                    <td>{row.jenis_pengelola}</td>
                    <td className="text-end">
                      {formatRupiah(row.total_belanja_bulanan)}
                    </td>
                    <td className="text-center">{row.jumlah_pengelola || 0}</td>
                    <td>
                      <Button
                        size="sm"
                        color="info"
                        outline
                        tag={Link}
                        to={`/admin/makan-minum/laporan-bulanan/${row.id}/edit`}
                        className="me-1"
                      >
                        Ubah
                      </Button>
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        onClick={() => handleDelete(row)}
                      >
                        Hapus
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LaporanBulananListPage;
